import { useState, useEffect, useCallback, useMemo } from "react";
import { 
  Calendar, 
  CalendarDays, 
  Mail, 
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  Eye,
  Plus,
  Edit,
  Users,
  User,
  LayoutDashboard,
  Upload,
  Image as ImageIcon,
  Images,
  Ticket,
  Building,
  Settings,
  Key
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminInternalBooking({ credentials, userRole }) {
  const [formData, setFormData] = useState({
    event_name: "",
    booking_date: "",
    start_time: "",
    end_time: "",
    organizer_name: "",
    contact_phone: "",
    expected_attendees: "",
    notes: "",
    is_recurring: false,
    recurrence_weeks: 4
  });
  const [submitting, setSubmitting] = useState(false);
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = useMemo(() => {
    const auth = btoa(`${credentials.username}:${credentials.password}`);
    return { Authorization: `Basic ${auth}` };
  }, [credentials.username, credentials.password]);

  const fetchInternalBookings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/bookings`, { headers });
      // Filter to show only internal bookings (those with "Internal:" prefix or is_internal flag)
      const internal = response.data.filter(b => b.is_internal || b.purpose_of_hire?.startsWith("Internal:"));
      setRecentBookings(internal.slice(0, 10));
    } catch (error) {
      console.error("Error fetching bookings:", error);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchInternalBookings();
  }, [fetchInternalBookings]);

  const resetForm = () => {
    setFormData({
      event_name: "",
      booking_date: "",
      start_time: "",
      end_time: "",
      organizer_name: "",
      contact_phone: "",
      expected_attendees: "",
      notes: "",
      is_recurring: false,
      recurrence_weeks: 4
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.event_name || !formData.booking_date || !formData.start_time || !formData.end_time) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings/internal`, {
        event_name: formData.event_name,
        booking_date: formData.booking_date,
        start_time: formData.start_time,
        end_time: formData.end_time,
        organizer_name: formData.organizer_name || null,
        contact_phone: formData.contact_phone || null,
        expected_attendees: formData.expected_attendees ? parseInt(formData.expected_attendees) : 0,
        notes: formData.notes || null,
        is_recurring: formData.is_recurring,
        recurrence_weeks: formData.is_recurring ? parseInt(formData.recurrence_weeks) || 4 : 1
      }, { headers });
      
      const successMsg = formData.is_recurring 
        ? `${formData.recurrence_weeks} weekly bookings created successfully`
        : "Internal booking created successfully";
      toast.success(successMsg);
      resetForm();
      fetchInternalBookings();
    } catch (error) {
      console.error("Error creating booking:", error);
      const message = error.response?.data?.detail || "Failed to create booking";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteBooking = async (id) => {
    try {
      await axios.delete(`${API}/bookings/${id}`, { headers });
      toast.success("Booking deleted");
      fetchInternalBookings();
    } catch (error) {
      toast.error("Failed to delete booking");
    }
  };

  return (
    <div data-testid="admin-internal-booking">
      <div className="flex justify-between items-center mb-8">
        <h1 className="heading-lg">Internal Booking</h1>
      </div>

      {/* Booking Form */}
      <Card className="card-heritage mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building className="w-5 h-5 text-[#14532D]" />
            Book Hall for Internal Use
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="label-heritage">Event Name *</Label>
              <Input
                value={formData.event_name}
                onChange={(e) => setFormData(prev => ({ ...prev, event_name: e.target.value }))}
                className="input-heritage"
                placeholder="e.g., Committee Meeting, Staff Training"
                required
                data-testid="internal-event-name"
              />
            </div>
            
            <div>
              <Label className="label-heritage">Date *</Label>
              <Input
                type="date"
                value={formData.booking_date}
                onChange={(e) => setFormData(prev => ({ ...prev, booking_date: e.target.value }))}
                className="input-heritage"
                required
                data-testid="internal-booking-date"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="label-heritage">Start Time *</Label>
                <Input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                  className="input-heritage"
                  required
                  data-testid="internal-start-time"
                />
              </div>
              <div>
                <Label className="label-heritage">End Time *</Label>
                <Input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                  className="input-heritage"
                  required
                  data-testid="internal-end-time"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="label-heritage">Organizer Name</Label>
                <Input
                  value={formData.organizer_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, organizer_name: e.target.value }))}
                  className="input-heritage"
                  placeholder="e.g., John Smith"
                  data-testid="internal-organizer-name"
                />
              </div>
              <div>
                <Label className="label-heritage">Contact Phone</Label>
                <Input
                  value={formData.contact_phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_phone: e.target.value }))}
                  className="input-heritage"
                  placeholder="e.g., 07700 123456"
                  data-testid="internal-contact-phone"
                />
              </div>
            </div>

            <div>
              <Label className="label-heritage">Expected Attendees</Label>
              <Input
                type="number"
                value={formData.expected_attendees}
                onChange={(e) => setFormData(prev => ({ ...prev, expected_attendees: e.target.value }))}
                className="input-heritage"
                placeholder="Approximate number of people"
                min="0"
                data-testid="internal-attendees"
              />
            </div>

            <div>
              <Label className="label-heritage">Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="input-heritage min-h-[80px]"
                placeholder="Any additional notes or requirements..."
                data-testid="internal-notes"
              />
            </div>

            {/* Recurring Booking Option */}
            <div className="bg-[#F5F5F0] rounded-lg p-4 space-y-4">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="is_recurring"
                  checked={formData.is_recurring}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_recurring: checked }))}
                  data-testid="internal-recurring-checkbox"
                />
                <Label htmlFor="is_recurring" className="font-medium text-[#1C1917] cursor-pointer">
                  Recurring weekly booking
                </Label>
              </div>
              
              {formData.is_recurring && (
                <div className="ml-7 space-y-3">
                  <div>
                    <Label className="label-heritage">Number of weeks</Label>
                    <Input
                      type="number"
                      value={formData.recurrence_weeks}
                      onChange={(e) => setFormData(prev => ({ ...prev, recurrence_weeks: e.target.value }))}
                      className="input-heritage w-32"
                      min="2"
                      max="52"
                      data-testid="internal-recurrence-weeks"
                    />
                    <p className="text-xs text-[#78716C] mt-1">Maximum 52 weeks</p>
                  </div>
                  
                  {formData.booking_date && formData.recurrence_weeks > 1 && (
                    <div className="bg-white rounded-lg p-3 border border-[#E7E5E4]">
                      <p className="text-sm font-medium text-[#14532D] mb-2">
                        Booking dates preview:
                      </p>
                      <div className="text-xs text-[#78716C] space-y-1 max-h-32 overflow-y-auto">
                        {Array.from({ length: Math.min(parseInt(formData.recurrence_weeks) || 0, 52) }, (_, i) => {
                          const baseDate = new Date(formData.booking_date + 'T12:00:00');
                          const date = new Date(baseDate);
                          date.setDate(date.getDate() + (i * 7));
                          return (
                            <div key={i} className="flex items-center gap-2">
                              <span className="w-6 text-center">{i + 1}.</span>
                              <span>{date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <Button 
              type="submit" 
              className="btn-primary w-full"
              disabled={submitting}
              data-testid="internal-booking-submit"
            >
              {submitting ? "Creating..." : formData.is_recurring ? `Create ${formData.recurrence_weeks} Weekly Bookings` : "Create Internal Booking"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Recent Internal Bookings */}
      <h2 className="heading-md mb-4">Recent Internal Bookings</h2>
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="spinner" />
        </div>
      ) : recentBookings.length === 0 ? (
        <Card className="card-heritage">
          <CardContent className="p-8 text-center">
            <Building className="w-12 h-12 text-[#D6D3D1] mx-auto mb-4" />
            <p className="text-[#78716C]">No internal bookings yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {recentBookings.map((booking) => (
            <Card key={booking.id} className="card-heritage">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-[#1C1917]">
                      {booking.purpose_of_hire?.replace("Internal: ", "") || booking.event_name}
                    </h3>
                    <div className="text-sm text-[#78716C] space-y-1">
                      <p>
                        <Calendar className="w-3 h-3 inline mr-1" />
                        {booking.booking_date} | {booking.start_time} - {booking.end_time}
                      </p>
                      {booking.hirer_name && booking.hirer_name !== "Internal Staff" && (
                        <p>
                          <User className="w-3 h-3 inline mr-1" />
                          {booking.hirer_name}
                          {booking.phone_number && booking.phone_number !== "Internal" && ` • ${booking.phone_number}`}
                        </p>
                      )}
                      {booking.number_of_attendees > 0 && (
                        <p>
                          <Users className="w-3 h-3 inline mr-1" />
                          {booking.number_of_attendees} attendees
                        </p>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-[#B91C1C]"
                    onClick={() => deleteBooking(booking.id)}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// Settings Component - Password Management (Admin Only)
